import {
  maybePromptPlanning,
  planningNotice,
  planningSettings,
} from "./planning-setup.js";
import { renderPlanManager } from "./plan-manager.js";
import { renderDashboard } from "./dashboard.js";
import { renderOwner, mountOwner, isOwner, disposeOwner } from "./owner.js";
import { l, t as translate, formatDate, languageSelect } from "../core/i18n.js";
import { icon } from "./icons.js";
import { enhanceWorkspace } from "./experience.js";
import { accountSection } from "./account.js";
import { syncRoute } from "../services/shell.js";
import { SHORT_PLAN_NAMES, WEEKDAYS, navItems } from "../core/config.js";
import {
  compFor,
  dateKey,
  ensureStudyFlow,
  ensureTodayStats,
  esc,
  examCountdownLabel,
  examDateLabel,
  examDaysLeft,
  fromKey,
  groupLabel,
  metrics,
  nextExam,
  planGroup,
  shortDate,
  sortedExams,
  studyItemsForToday,
  studySetting,
  tasksFor,
  weekday,
} from "../core/model.js";
import { bindPage } from "./events.js";
import { renderStudyWidget } from "./timers.js";
import { renderPomodoroPage } from "./pomodoro-page.js";
import { syncFloatingTimer } from "./floating-timer.js";
import { runtime } from "../core/runtime.js";
import {
  renderMonthlyPlanTable,
  mountMonthlyTable,
  tableMonthDays,
  resetTableView,
} from "./plan-table-view.js";

export function renderNav() {
  const button = ([id, label]) =>
    `<button class="${runtime.page === id ? "active" : ""}" data-page="${id}">${icon(id === "today" ? "dashboard" : id)}<span>${translate(label)}</span></button>`;
  const primary = ["today", "weekly", "table", "pomodoro", "exams"];
  const items = primary.map((id) => navItems.find(([key]) => key === id));
  document.getElementById("sideNav").innerHTML =
    items.map(button).join("") +
    `<div class="nav-divider" aria-hidden="true"></div><div class="nav-group-label">${translate("TOOLS")}</div>` +
    navItems
      .filter(([id]) => ["manage", "settings"].includes(id))
      .map(button)
      .join("") +
    (isOwner() ? button(["owner", "Owner"]) : "");
  document.getElementById("mobileNav").innerHTML = items
    .slice(0, 4)
    .map(button)
    .join("");
}

export function render() {
  if (runtime.page === "table" && runtime.lastRenderedPage !== "table")
    resetTableView();
  runtime.lastRenderedPage = runtime.page;
  disposeOwner();
  ensureTodayStats();
  renderNav();
  renderExamQuick();
  document.body.classList.remove("table-view");
  const datedPage = ["today", "weekly", "all"].includes(runtime.page);
  document.getElementById("dateNavigation").hidden = !datedPage;
  document.getElementById("navDate").textContent = formatDate(
    runtime.selectedDate,
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  );
  document.getElementById("navWeekday").textContent = weekday(
    runtime.selectedDate,
  );
  document.getElementById("todayBtn").disabled =
    dateKey(runtime.selectedDate) === dateKey(new Date());
  document
    .getElementById("prevBtn")
    .setAttribute(
      "aria-label",
      runtime.page === "table" ? l("Previous period") : l("Previous day"),
    );
  document
    .getElementById("nextBtn")
    .setAttribute(
      "aria-label",
      runtime.page === "table" ? l("Next period") : l("Next day"),
    );
  const p = document.getElementById("appPage");
  p.style.paddingBottom = "";
  p.classList.toggle("sheet-page", runtime.page === "table");
  if (runtime.page === "today")
    p.innerHTML = renderDaily(
      dateKey(runtime.selectedDate) === dateKey(new Date()),
    );
  else if (runtime.page === "weekly") p.innerHTML = renderWeekly();
  else if (runtime.page === "table") p.innerHTML = renderTable();
  else if (runtime.page === "exams") p.innerHTML = renderExams();
  else if (runtime.page === "all") p.innerHTML = renderAll();
  else if (runtime.page === "manage") p.innerHTML = renderManage();
  else if (runtime.page === "pomodoro") p.innerHTML = renderPom();
  else if (runtime.page === "owner") p.innerHTML = renderOwner();
  else p.innerHTML = renderSettings();
  if (["today", "weekly"].includes(runtime.page))
    p.insertAdjacentHTML("afterbegin", planningNotice());
  syncRoute();
  bindPage();
  if (runtime.page === "owner") mountOwner();
  enhanceWorkspace();
  renderStudyWidget();
  syncFloatingTimer();
  if (!["owner", "table"].includes(runtime.page))
    queueMicrotask(maybePromptPlanning);
  if (runtime.page === "table") mountMonthlyTable();
}

export function renderDaily(isToday) {
  return renderDashboard(isToday, taskHTML, timerDisplay());
}

export function renderPlanEmpty(group, compact = false) {
  return `<section class="plan-onboarding planning-plan-empty ${compact ? "compact" : ""}"><span class="planning-empty-icon">${icon("today")}</span><div><span class="plan-onboarding-kicker">${translate("YOUR FRESH START")}</span><h2>${translate("Start with one priority.")}</h2><p>${translate("Add a plan above. You can change it anytime.")}</p><button class="btn black" data-create-plan="${esc(group)}">${icon("plus")}${translate("Add plan")}</button><button class="btn" data-planning-setup>${translate("Planning setup")}</button></div></section>`;
}

export function renderExamQuick() {
  const quick = document.getElementById("quickExam"),
    exam = nextExam();
  quick.classList.toggle("active", runtime.page === "exams");
  quick.innerHTML = exam
    ? `<span class="exam-quick-dot"></span><span class="exam-quick-name">${esc(exam.name)}</span><strong>${examDaysLeft(exam.date) === 0 ? l("Today") : `${examDaysLeft(exam.date)}d`}</strong>`
    : `${l('<span class="exam-quick-dot"></span><span>Add exam date</span>')}`;
  quick.title = exam
    ? `${exam.name} - ${examDateLabel(exam.date)} (${examCountdownLabel(examDaysLeft(exam.date))})`
    : l("Add an exam date");
}

export function renderExamPreview() {
  const exam = nextExam();
  return exam
    ? `${l('<section class="exam-preview"><div class="exam-preview-main"><div class="exam-overline">NEXT EXAM</div><strong>')}${esc(exam.name)}</strong><span>${esc(examDateLabel(exam.date))}</span></div><div class="exam-preview-count"><b>${examDaysLeft(exam.date)}</b><span>${examDaysLeft(exam.date) === 0 ? l("EXAM DAY") : l("DAYS LEFT")}${l('</span></div><button class="exam-preview-link" data-open-exams aria-label="View exam dates">View all <span aria-hidden="true">→</span></button></section>')}`
    : `${l('<section class="exam-preview exam-preview-empty"><div class="exam-preview-main"><div class="exam-overline">EXAM DATES</div><strong>Keep your deadlines in sight</strong><span>Add a date to start the countdown.</span></div><button class="btn black" data-add-exam>Add exam date</button></section>')}`;
}

export function examCard(exam, featured = false) {
  const days = examDaysLeft(exam.date),
    past = days < 0,
    date = fromKey(exam.date),
    month = formatDate(date, { month: "short" }).toUpperCase();
  return `<article class="exam-card ${featured ? "featured" : ""} ${past ? "past" : ""} ${days >= 0 && days <= 7 ? "soon" : ""}"><div class="exam-card-date"><span>${month}</span><b>${date.getDate()}</b><small>${date.getFullYear()}</small></div><div class="exam-card-main"><div class="exam-card-label">${past ? l("PAST EXAM") : featured ? l("NEXT UP") : l("UPCOMING")}</div><h3>${esc(exam.name)}</h3><p>${esc(examDateLabel(exam.date))}</p>${exam.notes ? `<div class="exam-card-notes">${esc(exam.notes)}</div>` : ""}</div><div class="exam-card-count"><b>${Math.abs(days)}</b><span>${days === 0 ? l("TODAY") : past ? (Math.abs(days) === 1 ? l("DAY AGO") : l("DAYS AGO")) : days === 1 ? l("DAY LEFT") : l("DAYS LEFT")}</span></div><div class="exam-card-actions"><button type="button" class="tiny" data-edit-exam="${esc(exam.id)}${l('">Edit</button><button type="button" class="tiny" data-delete-exam="')}${esc(exam.id)}${l('">Delete</button></div></article>')}`;
}

export function renderExams() {
  const all = sortedExams(),
    upcoming = all.filter((x) => examDaysLeft(x.date) >= 0),
    past = all.filter((x) => examDaysLeft(x.date) < 0).reverse(),
    next = upcoming[0];
  return `${l('<div class="exam-hero"><div><div class="eyebrow">EXAM DATES</div><h1>Know what comes next.</h1><p>Add your exams and see exactly how many days remain.</p></div><button class="btn black" data-add-exam>+ Add exam</button></div>')}${next ? `${l('<div class="exam-feature"><div><span>NEXT EXAM</span><h2>')}${esc(next.name)}</h2><p>${esc(examDateLabel(next.date))}</p></div><div class="exam-feature-count"><b>${examDaysLeft(next.date)}</b><span>${examDaysLeft(next.date) === 0 ? l("EXAM DAY") : l("DAYS TO GO")}</span></div></div>` : ""}${l('<div class="exam-list-head"><h2>Upcoming exams</h2><span>')}${upcoming.length}${l(" scheduled</span></div>")}${upcoming.length ? `<div class="exam-list">${upcoming.map((x, i) => examCard(x, i === 0)).join("")}</div>` : `${l('<div class="exam-list-empty"><strong>No upcoming exams</strong><p>Add a date to start your countdown.</p><button class="btn black" data-add-exam>+ Add exam</button></div>')}`}${past.length ? `${l('<div class="exam-list-head exam-past-head"><h2>Past exams</h2><span>')}${past.length}${l(' saved</span></div><div class="exam-list">')}${past.map((x) => examCard(x)).join("")}</div>` : ""}`;
}

export function taskHTML(t, done) {
  const future = dateKey(runtime.selectedDate) > dateKey(new Date());
  return `<div class="task ${done ? "done" : ""} ${t.study ? "study-task" : ""}" data-task-id="${esc(t.id)}"><input class="task-check" type="checkbox" ${done ? "checked" : ""} ${future ? l('disabled title="Future days cannot be completed yet"') : ""} aria-label="${translate("Complete {name}", { name: esc(t.name) })}"><div class="task-main"><div class="task-name"><span>${esc(t.name)}</span></div>${t.schedule || t.study ? `<div class="task-meta">${t.schedule ? esc(t.schedule) : ""}${t.schedule && t.study ? " · " : ""}${t.study ? translate("Deep work") : ""}</div>` : ""}${t.notes ? `<div class="task-notes">${esc(t.notes)}</div>` : ""}</div><div class="task-actions"><button class="tiny" data-edit-task="${esc(t.id)}" aria-label="${esc(translate("Edit {name}", { name: t.name }))}" title="${translate("Edit")}">${icon("edit")}</button><button class="tiny" data-delete-task="${esc(t.id)}" aria-label="${esc(translate("Delete {name}", { name: t.name }))}" title="${translate("Delete")}">${icon("trash")}</button></div></div>`;
}

export function renderWeekly() {
  const monday = new Date(runtime.selectedDate);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const days = WEEKDAYS.map((name, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return { name, date, m: metrics(date) };
  });
  const done = days.reduce((n, d) => n + d.m.done, 0),
    total = days.reduce((n, d) => n + d.m.total, 0),
    pct = total ? Math.round((done / total) * 100) : 0;
  if (!total)
    return `${l('<div class="hero"><div><div class="eyebrow">YOUR WEEK, WITH INTENTION</div><h1 class="title">Week of ')}${esc(shortDate(monday))}${l('</h1><div class="subtitle">Build a rhythm for the days ahead.</div></div></div>')}${renderPlanEmpty(planGroup(runtime.selectedDate))}`;
  return (
    l(
      '<div class="hero"><div><div class="eyebrow">YOUR WEEK, WITH INTENTION</div><h1 class="title">Week of ',
    ) +
    shortDate(monday) +
    l(
      '</h1><div class="subtitle">Seven days. Plenty of possibility.</div></div></div><div class="weekly">',
    ) +
    days
      .map(
        ({ name, date, m }) =>
          '<button type="button" class="day-card ' +
          (dateKey(date) === dateKey(new Date()) ? "today" : "") +
          '" data-open-date="' +
          dateKey(date) +
          '"><span class="day-name">' +
          translate(name).slice(0, 3).toUpperCase() +
          '</span><div class="date">' +
          shortDate(date) +
          '</div><div class="num">' +
          m.done +
          " / " +
          m.total +
          '</div><div class="subtitle">' +
          m.pct.toFixed(1) +
          '%</div><div class="mini-bar"><i style="width:' +
          m.pct +
          '%"></i></div><div class="status-sm">' +
          m.status +
          "</div></button>",
      )
      .join("") +
    "</div>" +
    l(
      '<div class="week-insights"><section class="panel week-momentum"><div class="eyebrow">THE BIGGER PICTURE</div><h2>Your week, at a glance.</h2><div class="week-summary"><strong>',
    ) +
    pct +
    "<small>%</small></strong><p><b>" +
    done +
    l(" of ") +
    total +
    l(
      ' plans completed</b><span>Every small win becomes momentum.</span></p></div><div class="week-chart" aria-label="Daily completion percentages">',
    ) +
    days
      .map(
        ({ name, m }) =>
          "<div><span>" +
          Math.round(m.pct) +
          '%</span><div class="week-chart-track"><i style="height:' +
          Math.max(3, m.pct) +
          '%"></i></div><b>' +
          translate(name).slice(0, 3) +
          "</b></div>",
      )
      .join("") +
    l(
      '</div></section><section class="week-reflection"><span aria-hidden="true">✧</span><div class="eyebrow">A GENTLE REMINDER</div><h2>Consistency over<br> <em>intensity.</em></h2><p>You don’t need a perfect week. Just a clear next step, and a little space to begin again.</p><button class="btn" data-page="manage">Shape your routine ',
    ) +
    icon("arrow") +
    "</button></section></div>"
  );
}

export function renderAll() {
  if (
    ![
      ...Object.values(runtime.state.templates),
      ...Object.values(runtime.state.custom.datedPlans || {}),
    ].some((tasks) => tasks?.length)
  )
    return `${l('<div class="hero"><div><div class="eyebrow">ALL DAYS / YOUR JOURNEY</div><h1 class="title">Your progress begins here.</h1><div class="subtitle">Your timeline grows with every plan you complete.</div></div></div>')}${renderPlanEmpty(planGroup(runtime.selectedDate))}`;
  const start = new Date(runtime.selectedDate);
  start.setDate(start.getDate() - 30);
  const days = Array.from({ length: 61 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
  return (
    l(
      '<div class="hero"><div><div class="eyebrow">ALL DAYS / YOUR JOURNEY</div><h1 class="title">Daily Timeline</h1><div class="subtitle">A little progress, day after day. Your 61-day view.</div></div></div><div class="all-list">',
    ) +
    days
      .map((d, i) => {
        const m = metrics(d),
          c = compFor(dateKey(d)),
          today = dateKey(d) === dateKey(new Date());
        const month =
          i === 0 || d.getMonth() !== days[i - 1].getMonth()
            ? '<h2 class="timeline-month">' +
              formatDate(d, {
                month: "long",
                year: "numeric",
              }) +
              "</h2>"
            : "";
        return (
          month +
          '<article class="all-day ' +
          (today ? "today-timeline" : "") +
          '"><div class="all-head"><div class="timeline-date"><span>' +
          formatDate(d, { month: "short" }) +
          "</span><b>" +
          d.getDate() +
          '</b></div><div class="timeline-main"><h3>' +
          weekday(d) +
          (today ? l(' <span class="timeline-today">TODAY</span>') : "") +
          "</h3><p>" +
          m.done +
          " / " +
          m.total +
          l(" completed · ") +
          m.pct.toFixed(1) +
          '%</p></div><button class="tiny" data-open-date="' +
          dateKey(d) +
          l('">Open day ') +
          icon("arrow") +
          l('</button></div><details class="timeline-detail"><summary>View ') +
          tasksFor(d).length +
          l(" plans <span>") +
          esc(m.status) +
          '</span></summary><div class="all-tasks">' +
          tasksFor(d)
            .map(
              (t) =>
                '<div class="all-task ' +
                (c[t.id] ? "done" : "") +
                '"><span>' +
                (c[t.id] ? "✓" : "○") +
                "</span>" +
                esc(t.name) +
                (t.schedule ? " — " + esc(t.schedule) : "") +
                "</div>",
            )
            .join("") +
          "</div></details></article>"
        );
      })
      .join("") +
    "</div>"
  );
}

export function renderTable() {
  return renderPlanTable(null, tableMonthDays());
}

export function shortPlanName(name) {
  return SHORT_PLAN_NAMES[name] || name;
}

export function renderPlanTable(group, days) {
  return renderMonthlyPlanTable(days, renderPlanCell);
}

export function renderPlanCell(key, task) {
  const future = key > dateKey(new Date()),
    done = !!runtime.state.completions[key]?.[task.id];
  const kind = future
    ? "future"
    : done
      ? "done"
      : key < dateKey(new Date())
        ? "missed"
        : "today";
  const label = future
    ? l("Future day")
    : done
      ? l("Completed")
      : kind === "missed"
        ? l("Not completed")
        : l("Mark completed");
  return `<td class="sheet-cell ${kind} ${task.study ? "study-column" : ""}" data-label="${esc(task.name)}"><input class="sheet-check" type="checkbox" data-complete-date="${key}" data-complete-task="${esc(task.id)}" aria-label="${esc(task.name)} — ${key}: ${label}" ${done && !future ? "checked" : ""} ${future ? "disabled" : ""}></td>`;
}

export function renderManage() {
  return renderPlanManager();
}

export function timerDisplay() {
  if (!runtime.focusTimer) {
    const minutes = runtime.state.pom.blocks[0]?.work || 25;
    return `${String(minutes).padStart(2, "0")}:00`;
  }
  let s = Math.max(0, Math.ceil(runtime.focusTimer.remaining / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export function studySummary() {
  const items = studyItemsForToday();
  return translate("{count} tasks · {minutes} min", {
    count: items.length,
    minutes: items.reduce((sum, item) => sum + item.minutes, 0),
  });
}

export function renderStudyPanel() {
  ensureStudyFlow();
  const group = planGroup(new Date()),
    tasks = tasksFor(new Date()),
    selected = studyItemsForToday();
  const rows = tasks
    .map((task) => {
      const setting = studySetting(group, task);
      return `<div class="pf-flow-row"><label><input type="checkbox" class="pf-check" data-study-enabled="${esc(task.id)}" ${setting.enabled ? "checked" : ""}><span>${esc(task.name)}</span></label><span class="pf-minutes"><input type="number" inputmode="numeric" min="1" max="600" step="1" value="${setting.minutes}" data-study-minutes="${esc(task.id)}" aria-label="${esc(translate("Minutes for {name}", { name: task.name }))}"><small>${translate("min")}</small></span></div>`;
    })
    .join("");
  return `<section class="pf-flow" aria-labelledby="pfFlowTitle"><div class="pf-flow-head"><div><h2 id="pfFlowTitle">${translate("Focus with your daily plan")}</h2><p>${translate("Tick the tasks, set minutes, and DayX runs them one after another.")}</p></div><span data-study-summary>${esc(studySummary())}</span></div>${tasks.length ? `<div class="pf-flow-list">${rows}</div><p class="pf-note">${translate("Deep work tasks include a short break inside their time.")}</p><button type="button" class="kun-primary pf-flow-start" data-study-start ${selected.length ? "" : "disabled"}>${icon("play")}${runtime.studyTimer ? translate("Restart flow") : translate("Start Study Flow")}</button>` : `<p class="pf-flow-empty">${translate("No plans for this day")}</p><button type="button" class="kun-link" data-page="manage">${translate("Manage Plans")}${icon("arrow")}</button>`}</section>`;
}

export function renderPom() {
  return renderPomodoroPage(renderStudyPanel(), timerDisplay());
}

export function renderSettings() {
  const content = `${l('<div class="hero"><div><div class="eyebrow">SETTINGS</div><h1 class="title">Preferences</h1><div class="subtitle">Your preferences, account and backups.</div></div></div><div class="settings-grid"><div class="panel setting-section"><h3>')}${translate("Language")}</h3><p>${translate("Choose your interface language.")}</p>${languageSelect()}</div><div class="panel setting-section tariff-settings"><div class="eyebrow">DayX</div><h3>${translate("Monthly plan")}</h3><div class="settings-price"><strong>$1</strong><span>${translate("/ month")}</span></div><p>${translate("All your tools. One simple price.")}</p><small>${translate("Payments are not enabled yet. You can continue using your workspace.")}</small></div>${accountSection()}${l('<div class="panel setting-section"><h3>Pomodoro</h3><p>Defaults can be adjusted directly in the Pomodoro sequence.</p>')}${settingToggle(l("Sound"), "sound", runtime.state.prefs.sound)}<button class="btn chime-preview" type="button" data-preview-chime>${icon("audio")}${translate("Preview completion sound")}</button>${settingToggle(l("Notifications"), "notifications", runtime.state.prefs.notifications)}${settingToggle(l("Pomodoro Loop"), "loop", runtime.state.prefs.loop)}${l('</div><div class="panel setting-section"><h3>Data</h3><p>Export everything as JSON or restore a previous export.</p><div class="data-actions"><button class="btn black" data-data="export">Export Data</button><button class="btn" data-data="import">Import Data</button><button class="btn" data-data="reset">Reset Data</button></div></div><div class="panel setting-section"><h3>Keyboard shortcuts</h3><p>N next day • P previous day • T today • Space pause/resume Pomodoro • R reset Pomodoro</p></div></div>')}`;
  return content.replace(
    '<div class="settings-grid">',
    '<div class="settings-grid">' + planningSettings(),
  );
}

export function settingToggle(label, key, val) {
  return `<div class="toggle-row"><span>${translate(label)}</span><button class="switch ${val ? "on" : ""}" data-setting-toggle="${key}"><i></i></button></div>`;
}

export function renderDatedTable() {
  return renderTable();
}
